import type { AddressObject, SystemFolder } from "@mailflare/shared";
import { format, isThisYear, isToday, isYesterday } from "date-fns";

/** Human-readable byte size (binary units). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"] as const;
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
}

/** Gmail-style relative timestamp for list rows. */
export function formatMessageDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (isToday(date)) return format(date, "HH:mm");
  if (isYesterday(date)) return "Yesterday";
  if (isThisYear(date)) return format(date, "d MMM");
  return format(date, "d MMM yyyy");
}

/** Full, accessible timestamp. */
export function formatFullDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "PPpp");
}

/** `Name <address>` or just the address. */
export function formatAddress(address: AddressObject): string {
  return address.name !== undefined && address.name.length > 0
    ? `${address.name} <${address.address}>`
    : address.address;
}

/** One or two uppercase initials for avatar fallbacks. */
export function initialsFor(address: AddressObject): string {
  const source =
    address.name !== undefined && address.name.length > 0 ? address.name : address.address;
  const parts = source.split(/[\s@.]+/u).filter((part) => part.length > 0);
  const first = parts[0]?.charAt(0) ?? "?";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

/** Title-cases a system folder for display. */
export function folderLabel(folder: SystemFolder): string {
  return folder.charAt(0).toUpperCase() + folder.slice(1);
}

/** True when an event target is a text-entry surface and shortcuts must be ignored. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Strips HTML to a plain-text preview. Used only for locally generated content. */
export function htmlToText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/giu, " ")
    .replace(/<[^>]+>/gu, " ")
    .replace(/&nbsp;/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}
